// src/components/calendar/CalendarScheduler.jsx
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import './calendar_timeline_theme.css';
import DrawerComponent from '../common/Drawer.component';
import booking from '../../module/booking';
import BookingsYachtEditPage from '../../pages/booking/edit/BookingsYachtEdit.page';
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Button from '../common/Button.component';
import YachtForm from '../yacht/YachtForm.component';
import { API as yachtsAPI } from '../../module/yachts/api';
import AgentNAForm from '../agentNA/AgentNAForm.component';
import { DayPilotScheduler, DayPilot } from '@daypilot/daypilot-lite-react';
import { getItem, saveItem } from '../../utility/Storage.utility';
import { useSelector } from 'react-redux';
import { useCalendar } from '../../core/calendar/hook/useCalendar';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const CalendarScheduler = ({
  onClick = () => {},
  startDate,
  endDate,
  onSuccess = () => {},
  calendar,
}) => {
  const {
    actions: { sort: sortAction },
    selector,
  } = calendar;
  const { sort } = useSelector(selector.data);
  const { events, resources } = useSelector(selector.data);
  const { handleChangeSort } = useCalendar(sortAction);

  const matchesLg = useMediaQuery('(min-width:1750px)');
  const matchesXl = useMediaQuery('(min-width:2500px)');
  const [open, setOpen] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [dateEvent, setDateEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [currentDay, setCurrentDay] = useState(dayjs(startDate));
  const [disabledNext, setDisabledNext] = useState(false);
  const [disabledPrev, setDisabledPrev] = useState(true);

  const height = useMemo(
    () => (matchesXl ? 1000 : matchesLg ? 750 : 600),
    [matchesLg, matchesXl]
  );

  const start = useMemo(
    () => dayjs(startDate).startOf('month').format('YYYY-MM-DD'),
    [startDate]
  );
  const end = useMemo(
    () => dayjs(endDate).endOf('month').format('YYYY-MM-DD'),
    [endDate]
  );
  const firstDay = useMemo(
    () => new DayPilot.Date(startDate).firstDayOfMonth(),
    [startDate]
  );
  const totDays = useMemo(
    () => dayjs(end).diff(dayjs(start), 'day') + 1,
    [start, end]
  );

  const onEventClick = useCallback(
    (args) => {
      const scrollX = scheduler?.getScrollX();
      const scrollY = scheduler?.getScrollY();
      setDateEvent({ x: scrollX, y: scrollY });
      setOpen(args.e.data);
    },
    [scheduler]
  );

  const handleSort = useCallback(
    (key) => {
      handleChangeSort({ ...sort, by: key });
    },
    [sort, handleChangeSort]
  );

  const oClickDetail = useCallback(() => {
    saveItem('booking_calendar_X', dateEvent?.x);
    saveItem('booking_calendar_Y', dateEvent?.y);
    onClick(open);
  }, [open, dateEvent, onClick]);

  const config = useMemo(() => {
    let scale = 'Day';
    let timeHeaders = [];
    let days = totDays;
    let startD = firstDay;

    if (viewMode === 'day') {
      scale = 'Hour';
      timeHeaders = [
        { groupBy: 'Day', format: 'dddd d MMM' },
        { groupBy: 'Hour', format: 'HH:mm' },
      ];
      days = 1;
      startD = new DayPilot.Date(dayjs(currentDay).format('YYYY-MM-DD'));
    } else if (viewMode === 'week') {
      scale = 'Day';
      const startOfWeek = dayjs(currentDay).startOf('week');
      const endOfWeek = dayjs(currentDay).endOf('week');

      const weekRange = `${startOfWeek.format('D')}–${endOfWeek.format('D MMM')}`;

      timeHeaders = [
        { groupBy: 'Week', format: `${weekRange}` },
        { groupBy: 'Day', format: 'd ddd' },
      ];
      days = 7;
      startD = new DayPilot.Date(
        dayjs(currentDay).format('YYYY-MM-DD')
      ).firstDayOfWeek();
    } else {
      scale = 'Day';
      timeHeaders = [{ groupBy: 'Month' }, { groupBy: 'Day', format: 'd ddd' }];
      days = totDays;
      startD = firstDay;
    }

    return {
      timeHeaders,
      scale,
      days,
      startDate: startD,
      eventMoveHandling: 'Disabled',
      eventResizeHandling: 'Disabled',
      width: '100%',
      theme: 'calendar_timeline_theme',
      cellWidth: 70,
      headerHeight: 30,
      timeRangeSelectedHandling: 'Disabled',
      cellSelection: false,
      rowHeaderWidthAutoFit: true,
      rowHeaderWidthAutoFitShrink: true,
      rowHeaderWidth: 260,
      eventHeight: 50,
      eventMinWidth: 30,
      useEventBoxes: 'Never',
      onEventClick: (args) => onEventClick(args),
      onTimeHeaderClicked: (args) => {
        if (viewMode === 'month') {
          const clicked = args?.header?.start;
          const selectedDay = clicked && dayjs(clicked.toString());
          setCurrentDay(selectedDay);
          setViewMode('day');

          setTimeout(() => {
            scheduler?.scrollTo(clicked);
          }, 50);
        }
      },
      onRowClick: (args) => {
        const target = args?.originalEvent?.target;

        if (target?.closest?.('.dp-yacht-link')) {
          args.originalEvent.preventDefault();
          args.originalEvent.stopPropagation();

          const url = args?.row?.data?.url_web;
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
        }
      },

      onBeforeRowHeaderRender: (args) => {
        const color = args?.row?.index % 2 === 0 ? '#f0f5f7' : '#fff';
        args.row.backColor = color;

        const hasUrl = !!args?.row?.data?.url_web;
        const provenence = args?.row?.data?.provenence;
        const mexalCode = args?.row?.data?.mexal_code;
        const mexalResearchName = args?.row?.data?.mexal_research_name;
        const mexalTooltip = [mexalCode, mexalResearchName]
          .filter((item) => item)
          .join(' - ');
        const mexalTooltipEscaped = (mexalTooltip || '').replaceAll('"', '&quot;');

        const boatIcon = hasUrl
          ? `<span class="dp-yacht-link" title="Yacht position"
        style="display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; cursor:pointer; color:#1976d2;">
        <svg width="16" height="16" viewBox="0 0 512 512" fill="currentColor">
          <path d="M247 40l-74.6 161.6 74.6 24.9V40zm18 0v186.5l74.6-24.9L265 40zm-86.7 105.8l-136.02 17 112.02 35 24-52zm155.4 0l24 52 112.1-35-136.1-17zM25.48 176.4L130 307.1l104.6-65.4-209.12-65.3zm461.02 0l-209.1 65.3L382 307.1l104.5-130.7zM256 249.6L159.4 310h193.2L256 249.6zM128 358c-16 0-32.14 9-45.68 17.3-13.53 8.4-23.93 16.7-23.93 16.7l11.22 14s9.65-7.7 22.14-15.3C104.2 383 120.1 376 128 376c7.9 0 16.4 6 26.4 14s21.6 18 37.6 18 27.6-10 37.6-18 18.5-14 26.4-14c7.9 0 16.4 6 26.4 14s21.6 18 37.6 18 27.6-10 37.6-18 18.5-14 26.4-14c7.9 0 23.8 7 36.2 14.7 12.5 7.6 22.2 15.3 22.2 15.3l11.2-14s-10.4-8.3-23.9-16.7C416.1 367 400 358 384 358c-16 0-27.6 10-37.6 18s-18.5 14-26.4 14c-7.9 0-16.4-6-26.4-14S272 358 256 358s-27.6 10-37.6 18-18.5 14-26.4 14c-7.9 0-16.4-6-26.4-14S144 358 128 358zm0 64c-16 0-32.14 9-45.68 17.3-13.53 8.4-23.93 16.7-23.93 16.7l11.22 14s9.65-7.7 22.14-15.3C104.2 447 120.1 440 128 440c7.9 0 16.4 6 26.4 14s21.6 18 37.6 18 27.6-10 37.6-18 18.5-14 26.4-14c7.9 0 16.4 6 26.4 14s21.6 18 37.6 18 27.6-10 37.6-18 18.5-14 26.4-14c7.9 0 23.8 7 36.2 14.7 12.5 7.6 22.2 15.3 22.2 15.3l11.2-14s-10.4-8.3-23.9-16.7C416.1 431 400 422 384 422c-16 0-27.6 10-37.6 18s-18.5 14-26.4 14c-7.9 0-16.4-6-26.4-14S272 422 256 422s-27.6 10-37.6 18-18.5 14-26.4 14c-7.9 0-16.4-6-26.4-14S144 422 128 422z"></path>
        </svg>
      </span>`
          : '';

        const provenenceHtml = provenence
          ? `<div style="font-size:11px; color:#757575; margin-top:2px;">${provenence}</div>`
          : '';

        args.row.html = `
          <div style="justify-content:space-between; display:flex;align-items:center; width:240px;">
          <div style="display:flex; flex-direction:column; gap:0px; overflow:hidden;">
            <div style="display:flex; align-items:center; gap:6px;">
              <span style="font-weight:bold" title="${mexalTooltipEscaped}">${args.row.name}</span>
              ${boatIcon}
            </div>
            ${provenenceHtml}
          </div>
            <span style="padding:4px 8px; border-radius:16px; background-color:rgba(0,0,0,0.08); color:#616161; font-size:12px; font-weight:500">
              #${args.row.id}
            </span>
          </div>`;
      },
      // onBeforeEventRender: (args) => {
      //   console.log('args.data', args.data);
      //   const data = args.data;
      //   args.data.toolTip = `
      //   ${data.marina}IN: ${data?.check_in_date}
      //   OUT:  ${data?.check_out_date}`;
      // },
    };
  }, [firstDay, onEventClick, totDays, viewMode, currentDay, scheduler]);

  const goPreviousDay = useCallback(() => {
    const newDay = currentDay.subtract(1, 'day');
    setDisabledPrev(false);
    setDisabledNext(false);

    if (
      newDay.isBefore(dayjs(startDate), 'day') ||
      newDay.isSame(dayjs(startDate), 'day')
    )
      setDisabledPrev(true);
    setCurrentDay(newDay);
    scheduler?.scrollTo(new DayPilot.Date(newDay.format('YYYY-MM-DD')));
  }, [currentDay, startDate, scheduler]);

  const goNextDay = useCallback(() => {
    const newDay = currentDay.add(1, 'day');
    setDisabledNext(false);
    setDisabledPrev(false);

    if (newDay.isAfter(dayjs(endDate), 'day')) setDisabledNext(true);
    setCurrentDay(newDay);
    scheduler?.scrollTo(new DayPilot.Date(newDay.format('YYYY-MM-DD')));
  }, [currentDay, endDate, scheduler]);

  const onChangeMode = useCallback(
    (view) => {
      if (view) {
        setViewMode(view);

        setDisabledNext(false);
        setDisabledPrev(false);
        if (
          currentDay.isBefore(dayjs(startDate), 'day') ||
          currentDay.isSame(dayjs(startDate), 'day')
        )
          setDisabledPrev(true);

        if (
          currentDay.isAfter(dayjs(endDate), 'day') ||
          currentDay.isSame(dayjs(endDate), 'day')
        )
          setDisabledNext(true);
      }
    },
    [currentDay, startDate, endDate]
  );

  useEffect(() => {
    const scrollX = getItem('booking_calendar_X');
    const scrollY = getItem('booking_calendar_Y');
    if (scheduler && scrollX && scrollY) {
      scheduler?.setScrollY(scrollY);
      scheduler?.setScrollX(scrollX);
    }
  }, [scheduler]);

  useEffect(() => {
    setCurrentDay(dayjs(startDate));
  }, [startDate]);

  return (
    <Fragment>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={'space-between'}
        mb={2}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, v) => onChangeMode(v)}
          color='primary'
          size='small'
        >
          <ToggleButton value='month'>Mese</ToggleButton>
          <ToggleButton value='day'>Giorno</ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'day' && (
          <Stack direction='row' spacing={1} marginTop={1}>
            <Button
              disabled={disabledPrev}
              onClick={() => goPreviousDay()}
              variant='outlined'
            >
              Prev
            </Button>
            <Button
              disabled={disabledNext}
              onClick={() => goNextDay()}
              variant='outlined'
            >
              Next
            </Button>
          </Stack>
        )}
      </Stack>

      {!!open && (
        <DrawerComponent defaultOpen={!!open} onClose={() => setOpen(null)}>
          <Stack spacing={2} width={1}>
            <BookingsYachtEditPage
              module={booking}
              id={open?.id}
              actions={
                <Stack>
                  <Stack direction={'row'} justifyContent={'space-between'}>
                    <Chip
                      label={`ID: ${open?.id}`}
                      color='primary'
                      variant='outlined'
                    />

                    <Button
                      startIcon={<VisibilityOutlinedIcon />}
                      variant='outlined'
                      onClick={() => oClickDetail()}
                    >
                      Booking Detail
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    <YachtForm api={yachtsAPI.selectWithAttendances} />
                    <AgentNAForm />
                  </Stack>
                </Stack>
              }
              onSuccess={onSuccess}
            />
          </Stack>
        </DrawerComponent>
      )}

      <Box position={'relative'}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{
            position: 'absolute',
            left: 8,
            top: 35,
            zIndex: 999,
            width: 245,
            cursor: 'pointer',
          }}
          spacing={2}
          onClick={() => handleSort('id_yacht.yacht_name')}
        >
          <Box display={'flex'} alignItems={'center'}>
            <Typography
              fontSize={15}
              fontWeight={'bold'}
              color={'#fff'}
              textTransform={'uppercase'}
            >
              Yacht Name
            </Typography>
            {sort?.direction === 'desc' ? (
              <ArrowDownwardIcon
                style={{
                  height: 20,
                  color:
                    sort?.by === 'id_yacht.yacht_name' ? 'grey' : 'transparent',
                }}
              />
            ) : (
              <ArrowUpwardIcon
                style={{
                  height: 20,
                  color:
                    sort?.by === 'id_yacht.yacht_name' ? 'grey' : 'transparent',
                }}
              />
            )}
          </Box>
        </Stack>

        <DayPilotScheduler
          {...config}
          resources={resources}
          events={events}
          height={height}
          heightSpec={'Max'}
          controlRef={setScheduler}
        />
      </Box>
    </Fragment>
  );
};

export default CalendarScheduler;
